import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { documentsActions } from '../../store/actions/documents.actions';
import {
  Drawer,
  Button,
  Form,
  Select,
  Icon,
  Popover,
  Switch,
  Typography,
  Tooltip,
} from 'antd';
import {
  dspxGetDocTypesChildrens,
  dspxGetChildrens,
  dspxGetChildrensDocumentContractor,
  dspxGetWorkerChildrens,
} from '../../../../helpers/dspx-utils';
import { useDispatch, useSelector } from 'react-redux';
import DateRangePicker from '../../../../components/DateRangePicker/DateRangePicker';
import useDocumentSelectors from './hooks/useDocumentSelectors';
import useNewDocumentSelectors from './hooks/useNewDocumentSelectors';
import styles from './FilterDrawer.module.scss';
import { saveDocumentFilter } from '../../store/actions/documentFilter/documentFilterActions';
import SavedFiltersList from '../SavedFiltersList/SavedFiltersList';
import AddressSelectGroup from '../../../../components/AddressSelectGroup/AddressSelectGroup';
import resetDocumentDefaultFilter from '../../requests/resetDocumentDefaultFilter';
import SaveFilter from '../../../../components/SaveFilter';
import { FILTER_NAME_VISIBLE_LENGTH } from '../../../../helpers/filterName';
import { SelectCompany } from '../../SelectCompany';
import { checkDocTypeHasSubtype } from '../../table/utils/checkDocTypeHasSubtype';
import { docTypes } from '../../constants/documents.constants';

const { Option } = Select;

const FilterDrawer = ({}) => {
  const dispatch = useDispatch();
  const [savePopoverVisible, setSavePopoverVisible] = useState(false);
  const {
    valueType,
    valueSubType,
    listAddrFlat,
    valueAddrFlat,
    valueAddrBuilding,
    valueAddrStreet,
    valueServiceCompany,
    valueExecuter,
    valueCreator,
    responsibleWorker,
    valueCompany,
    valueStatus,
    filterStatus,
    dateRange,
    selectedDateRange,
    dateRangeField,
    filterVisible,
    listAddrBuilding,
    viewOtherDocs,
  } = useDocumentSelectors();

  const {
    listAddrStreet,
    listDocumentType,
    listDocumentOrdSubtype,
    listDocumentOssSubtype,
    listDocumentContractSubtype,
    listExecuter,
    responsibleWorkers,
    listServiceCompanies,
  } = useNewDocumentSelectors();

  const documentContractors = useSelector(
    (store) => store.contractorStore.documentContractors
  );

  const chosenFilterId = useSelector(
    (store) => store.documentFilterStore.chosenFilterId
  );

  const userInfo = useSelector((store) => store.authentication.userInfo);

  const handleSelectedRange = useCallback((range) => {
    dispatch(documentsActions.setSelectedDateRange(range));
  }, []);

  const closePopover = useCallback(() => {
    setSavePopoverVisible(false);
  }, []);

  const saveFilter = useCallback(({ filterName }) => {
    dispatch(saveDocumentFilter(filterName));
    setSavePopoverVisible(false);
  }, [dispatch, saveDocumentFilter]);

  const handleStreetChange = useCallback((value) =>
    dispatch(documentsActions.applyAddrStreet(value))
  );
  const handleHouseChange = useCallback((value) =>
    dispatch(documentsActions.applyAddrBuilding(value))
  );
  const handleFlatChange = useCallback((value) =>
    dispatch(documentsActions.setAddrFlatValue(value))
  );

  const handleResetDefaultFilter = useCallback(() => {
    resetDocumentDefaultFilter(chosenFilterId);
    dispatch(documentsActions.resetFilter());
  }, [chosenFilterId]);

  const saveForm = <div className={styles.saveFilterPopover}>
    <SaveFilter
      onSubmit={saveFilter}
      onCancel={closePopover}
      maxNameLength={FILTER_NAME_VISIBLE_LENGTH}
    />
  </div>

  const handleSaveFilterVisibleChange = useCallback((visible) => {
    setSavePopoverVisible(visible);
  }, []);

  const showSubtype = (docTypeIds) =>
    Array.isArray(docTypeIds) &&
    docTypeIds.length > 0 &&
    docTypeIds.some(checkDocTypeHasSubtype);

  const showCurrentSubtypes = showSubtype(valueType);

  const listDocumentSubtypeGroups = useMemo(() => {
    if (!showCurrentSubtypes) {
      return [];
    }
    const typeToSubtypes = new Map([
      [docTypes.organizational, listDocumentOrdSubtype],
      [docTypes.meeting, listDocumentOssSubtype],
      [docTypes.contract, listDocumentContractSubtype],
    ]);

    const groups = [];

    typeToSubtypes.forEach((subTypes, typeId) => {
      if (!valueType.includes(typeId)) {
        return;
      }
      groups.push({
        typeId,
        type: listDocumentType.find((t) => t.id === typeId),
        subTypes,
      });
    });

    return groups;
  }, [
    listDocumentContractSubtype,
    listDocumentOrdSubtype,
    listDocumentOssSubtype,
    listDocumentType,
    showCurrentSubtypes,
    valueType,
  ]);

  const handleServiceCompanyChange = useCallback(
    (companyId) => {
      if (companyId === valueServiceCompany) {
        return;
      }

      if (!companyId) {
        dispatch(documentsActions.applyServiceCompany(undefined));
        dispatch(documentsActions.applyExecuter(undefined));
        dispatch(documentsActions.applyResponsible(undefined));
        return;
      }

      dispatch(documentsActions.applyServiceCompany(companyId));
      if (valueExecuter) {
        const executor = listExecuter.find(
          (e) => e.id === Number(valueExecuter)
        );
        if (executor?.serviceCompanyId !== companyId) {
          dispatch(documentsActions.applyExecuter(undefined));
        }
      }
      if (responsibleWorker) {
        const respWorker = listExecuter.find(
          (e) => e.id === Number(valueExecuter)
        );
        if (respWorker?.companyId !== companyId) {
          dispatch(documentsActions.applyResponsible(undefined));
        }
      }
    },
    [
      dispatch,
      listExecuter,
      responsibleWorker,
      valueExecuter,
      valueServiceCompany,
    ]
  );

  useEffect(() => {
    if (valueServiceCompany) {
      return;
    }
    if (
      listServiceCompanies &&
      (listServiceCompanies.length === 0 || listServiceCompanies.length > 1)
    ) {
      return;
    }
    const compId = listServiceCompanies[0].id;
    if (!compId) {
      return;
    }
    handleServiceCompanyChange(compId);
  }, [
    handleServiceCompanyChange,
    JSON.stringify(listServiceCompanies),
    valueServiceCompany,
  ]);

  return (
    <Drawer
      title="Фильтр документов"
      drawerStyle={{ backgroundColor: '#f9f9f9' }}
      width={530}
      closable={true}
      visible={filterVisible}
      onClose={() => dispatch(documentsActions.closeFilter(false))}
    >
      <div className={styles.filterForm}>
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            gap: '16px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <SelectCompany
              companyId={valueServiceCompany}
              companies={listServiceCompanies}
              onChange={handleServiceCompanyChange}
              disabled={false}
            />
          </div>
        </div>

        {userInfo.SwitchToVisibility && (
          <label className={styles.filterSwitch}>
            <Switch
              checked={viewOtherDocs}
              onChange={(newVal) =>
                dispatch(documentsActions.setViewOtherDocs(newVal))
              }
            />
            <Typography.Text>
              Посмотреть все документы сотрудников{' '}
              <Tooltip
                title={`
                  При активации данной кнопки вам будут показаны документы сотрудников,
                  в которых вы не являетесь задействованным лицом. 
                  Будьте аккуратны.
                `}
                placement="bottomLeft"
              >
                <Icon type="info-circle" />
              </Tooltip>
            </Typography.Text>
          </label>
        )}

        <div className={styles.filtersList}>
          <SavedFiltersList />
        </div>

        <div className={styles.filterDate}>
          <DateRangePicker
            storedDatesRange={dateRange}
            getDataByRangeAction={documentsActions.setDateRange}
            handleSelectedRange={handleSelectedRange}
            storedSelectedRange={selectedDateRange}
          />

          <Select
            defaultValue="create_time"
            value={dateRangeField}
            className={styles.longSelect}
            onChange={(selectValue) =>
              dispatch(documentsActions.setDateRangeField(selectValue))
            }
          >
            <Option value="create_time">Создание</Option>
            <Option value="register_date">Регистрация</Option>
            <Option value="execution_date">Выполнение</Option>
          </Select>
        </div>
        <div className={styles.filterParams}>
          <div
            className={styles.filterParamsRow}
            style={
              showCurrentSubtypes
                ? {
                    flexWrap: 'wrap',
                    rowGap: '16px',
                  }
                : {}
            }
          >
            <Select
              mode="multiple"
              defaultActiveFirstOption={false}
              allowClear={true}
              className={styles.longSelect}
              placeholder="Тип документа"
              value={valueType}
              onChange={(filter) => {
                dispatch(documentsActions.applyType(filter))
                if (!showSubtype(filter)) {
                  dispatch(documentsActions.applySubType([]));
                }
              }}
              optionFilterProp="searchtext"
            >
              {dspxGetDocTypesChildrens(listDocumentType)}
            </Select>
            {showCurrentSubtypes && (
              <>
                <Select
                  mode="multiple"
                  defaultActiveFirstOption={false}
                  allowClear={true}
                  className={styles.longSelect}
                  placeholder="Подтип документа"
                  value={valueSubType}
                  onChange={(filter) =>
                    dispatch(documentsActions.applySubType(filter))
                  }
                  optionFilterProp="searchtext"
                >
                  {listDocumentSubtypeGroups.map((group) => (
                    <Select.OptGroup label={group.type.name}>
                      {dspxGetDocTypesChildrens(group.subTypes, true)}
                    </Select.OptGroup>
                  ))}
                </Select>
                <br
                  style={{
                    flexBasis: '100%',
                    height: 0,
                  }}
                />
              </>
            )}
            <Select
              mode="multiple"
              defaultActiveFirstOption={false}
              allowClear={true}
              className={styles.longSelect}
              placeholder="Статус документа"
              value={valueStatus}
              onChange={(filter) =>
                dispatch(documentsActions.applyStatus(filter))
              }
              optionFilterProp="searchtext"
            >
              {dspxGetChildrens(filterStatus)}
            </Select>
          </div>

          <div className={styles.filterParamsRow}>
            <Select
              defaultActiveFirstOption={false}
              allowClear={true}
              className={styles.longSelect}
              placeholder="Исполнитель"
              value={valueExecuter || undefined}
              onChange={(executorId) => {
                dispatch(documentsActions.applyExecuter(executorId));
                const executor = listExecuter.find(
                  (e) => e.id === Number(executorId)
                );

                if (
                  executor &&
                  (!valueServiceCompany ||
                    valueServiceCompany !== executor.serviceCompanyId)
                ) {
                  const company = (listServiceCompanies || []).find(
                    (c) => c.id === executor.serviceCompanyId
                  );
                  dispatch(documentsActions.applyServiceCompany(company?.id));
                }
              }}
              optionLabelProp="label"
              optionFilterProp="searchtext"
            >
              {dspxGetWorkerChildrens(
                listExecuter.filter((e) =>
                  valueServiceCompany
                    ? valueServiceCompany === e.serviceCompanyId
                    : true
                )
              )}
            </Select>

            <Select
              defaultActiveFirstOption={false}
              allowClear={true}
              className={styles.longSelect}
              placeholder="Ответственнный"
              value={responsibleWorker || undefined}
              onChange={(responsibleWorkerId) => {
                dispatch(
                  documentsActions.applyResponsible(responsibleWorkerId)
                );
                const responsibleWorker = responsibleWorkers.find(
                  (rw) => rw.id === Number(responsibleWorkerId)
                );

                if (
                  responsibleWorker &&
                  (!valueServiceCompany ||
                    valueServiceCompany !== responsibleWorker.companyId)
                ) {
                  const company = (listServiceCompanies || []).find(
                    (c) => c.id === responsibleWorker.companyId
                  );
                  dispatch(documentsActions.applyServiceCompany(company?.id));
                }
              }}
              optionLabelProp="label"
              optionFilterProp="searchtext"
            >
              {dspxGetWorkerChildrens(
                responsibleWorkers.filter((rw) =>
                  valueServiceCompany?.id
                    ? valueServiceCompany?.id === rw.companyId
                    : true
                )
              )}
            </Select>
          </div>

          <div className={styles.filterParamsRow}>
            <Select
              mode="multiple"
              allowClear={true}
              className={styles.longSelect}
              placeholder="Автор"
              value={valueCreator}
              onChange={(filter) => {
                console.log(valueCreator, filter);
                dispatch(documentsActions.applyCreator(filter));
              }}
              optionFilterProp="searchtext"
            >
              {dspxGetChildrens(listExecuter)}
            </Select>
            <Select
              mode="multiple"
              defaultActiveFirstOption={false}
              allowClear={true}
              className={styles.longSelect}
              placeholder="Контрагент"
              value={valueCompany}
              onChange={(filter) => {
                dispatch(documentsActions.applyCompany(filter));
              }}
              optionFilterProp="searchtext"
            >
              {dspxGetChildrensDocumentContractor(documentContractors)}
            </Select>
          </div>
        </div>
        <Form.Item label="Адрес клиента">
          <AddressSelectGroup
            streets={listAddrStreet}
            flats={listAddrFlat}
            houses={listAddrBuilding}
            showSearch
            street={valueAddrStreet}
            house={valueAddrBuilding}
            flat={valueAddrFlat}
            handleStreetChange={handleStreetChange}
            handleHouseChange={handleHouseChange}
            handleFlatChange={handleFlatChange}
            multiple
          />
        </Form.Item>
        <div className={styles.filterButtons}>
          <Popover
            content={saveForm}
            title="Введите название для фильтра"
            trigger="click"
            placement="top"
            onVisibleChange={handleSaveFilterVisibleChange}
            visible={savePopoverVisible}
          >
            <Button className={styles.resetFilterButton} block>
              <div className={styles.filterButtonIconContents}>
                <Icon type="save" className={styles.filterButtonIcon} />

                <div>Сохранить фильтр</div>
              </div>
            </Button>
          </Popover>

          <Button
            className={styles.resetFilterButton}
            block
            onClick={handleResetDefaultFilter}
          >
            <div className={styles.filterButtonIconContents}>
              <Icon type="sync" className={styles.filterButtonIcon} />
              <div>Сбросить фильтр</div>
            </div>
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default memo(FilterDrawer);
